import * as React from 'react'
import type { Params } from 'react-router-dom'
import { Outlet, useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { TimesheetReferenceContractsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import EmptyList from 'modules/core/components/EmptyList'
import Loading from 'modules/core/components/Loading'
import { Employee, useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import AdminLayout from 'modules/attendance/components/ClockIn/AdminLayout'
import TimesheetButtons from 'modules/attendance/components/ClockIn/AdminLayout/WeeklyView/TimesheetButtons'
import Shifts from 'modules/attendance/components/ClockIn/Shifts'
import ClockInStore from 'modules/attendance/components/ClockIn/Store'
import type Store from 'modules/attendance/components/ClockIn/Store'
import type { StoreProps } from 'modules/attendance/components/ClockIn/types'
import {
  Cycle,
  useAttendanceDateNavigator,
} from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import {
  AttendancePoliciesProvider,
  useAttendancePolicies,
} from 'modules/attendance/lib/attendancePolicies'
import { ContractsVersionProvider } from 'modules/attendance/lib/contractsVersionContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'

interface Props {
  params: Params
  projectsFeatureAvailability: boolean
  currentEmployeeId: number | null | undefined
  currentEmployeeAttendanceEmployeesSettingId?: number | null
  dateFrom: Date
  dateTo: Date
  cycle: Cycle
}

const RenderTable = ({
  showHourBalance,
  store,
  isDaily,
}: {
  showHourBalance: boolean
  store: Store
  isDaily: boolean
}) => {
  const { attendancePeriods } = useTimesheetData()

  if (!attendancePeriods?.length) {
    return (
      <>
        {isDaily && (
          <TimesheetButtons
            employeeId={store.employeeId}
            showHourBalance={showHourBalance}
            isDaily
          />
        )}
        <EmptyList description={i18n.t('clock_in.empty_list.description')} />
      </>
    )
  }

  return <Shifts store={store} showHourBalance={showHourBalance} isDaily={isDaily} />
}

const EmployeePeriodShifts = (props: Props) => {
  const {
    params: { year, month, employeeId },
    currentEmployeeId,
    dateFrom,
    dateTo,
    cycle,
  } = props

  const { attendanceEmployeesPolicyId } = useAttendancePolicies()

  const coercedProps: StoreProps = {
    year: Number(year),
    month: Number(month),
    employeeId: Number(employeeId),
    currentEmployeeId,
    layout: 'admin',
  }
  const store = new ClockInStore(coercedProps)

  const hasForfaitJour = hasFeature(TimesheetFeatures.FORFAIT_JOUR)
  const hasComplementaryHours = hasFeature(TimesheetFeatures.COMPLEMENTARY_HOURS)

  const { data: referenceContractData, loading: loadingReferenceContract } = useQuery(
    TimesheetReferenceContractsDocument,
    {
      variables: {
        employeeIds: [store.employeeId],
        referenceOn: moment().format('YYYY-MM-DD'),
      },
      skip: !hasForfaitJour && !hasComplementaryHours,
    }
  )

  const contract = referenceContractData?.contracts.referenceContractsConnection.nodes[0]
  const isDaily = hasForfaitJour && !!contract?.frForfaitJours
  const isPartTime = !!contract?.partTime

  const fetch = async () => {
    setLoading(true)

    await store.fetch(moment(dateFrom), moment(dateTo))
    setLoading(false)
  }

  React.useEffect(() => {
    fetch()
  }, [cycle, year, month, dateFrom, employeeId])

  const [loading, setLoading] = React.useState(false)

  const { featureAvailability: canSeeHourBalance } =
    useAttendanceFeatures('hour_balance_visibility')
  const { featureAvailability: hasHourBalanceUI } = useAttendanceFeatures('dev_hour_balance_ui')
  const showHourBalance = hasHourBalanceUI && canSeeHourBalance

  if (loadingReferenceContract) {
    return <Loading />
  }

  return (
    <AdminLayout
      employeeId={Number(employeeId)}
      attendanceEmployeesPolicyId={attendanceEmployeesPolicyId}
      showHourBalance={showHourBalance}
      isDaily={isDaily}
      isPartTime={isPartTime}
    >
      {loading && <Loading />}
      <Outlet />
      {!loading && (
        <RenderTable showHourBalance={showHourBalance} store={store} isDaily={isDaily} />
      )}
    </AdminLayout>
  )
}

const EmployeePeriodShiftsWrapper = ({ employee }: { employee: Employee }) => {
  const params = useParams()
  const { featureAvailability: projectsFeature } = useAttendanceFeatures(
    Features.projects_management
  )
  const { dateFrom, dateTo, cycle } = useAttendanceDateNavigator()

  return (
    <EmployeePeriodShifts
      params={params}
      projectsFeatureAvailability={projectsFeature}
      currentEmployeeId={employee?.id}
      dateFrom={dateFrom}
      dateTo={dateTo}
      cycle={cycle}
    />
  )
}

const EmployeePeriodShiftsPage = () => {
  const { employee } = useCurrentSession()
  const params = useParams()
  const { stringDateFrom: startOn } = useAttendanceDateNavigator()

  return (
    <ContractsVersionProvider employeeId={Number(params.employeeId)} activeDate={startOn}>
      <AttendancePoliciesProvider employeeId={Number(params.employeeId)}>
        <EmployeePeriodShiftsWrapper employee={employee} />
      </AttendancePoliciesProvider>
    </ContractsVersionProvider>
  )
}

export default EmployeePeriodShiftsPage
